/**
 * Utility functions for workspace-aware navigation & shared configs
 */
import 'quill-mention/dist/quill.mention.min.css';
import 'quill-mention';

// Shared mutable ref so the source callback always reads the latest members
let _mentionMembers = [];
let _mentionConfig = null;

export function setMentionMembers(members) {
  _mentionMembers = members || [];
}

export function getMentionModule() {
  if (_mentionConfig) return _mentionConfig;
  _mentionConfig = {
    allowedChars: /^[A-Za-z\s]*$/,
    mentionDenotationChars: ['@'],
    positioningStrategy: 'fixed',
    defaultMenuOrientation: 'bottom',
    dataAttributes: ['id', 'value'],
    source(searchTerm, renderList) {
      const values = _mentionMembers.map(m => {
        const name = m['firstName'] ? `${m['firstName']} ${m['lastName'] || ''}`.trim() : m['email'];
        return { id: m['_id'], value: m['selected'] ? `${name} (you)` : name };
      });
      if (!searchTerm) return renderList(values, searchTerm);
      const term = searchTerm.toLowerCase();
      renderList(values.filter(v => v.value.toLowerCase().includes(term)), searchTerm);
    },
  };
  return _mentionConfig;
}

/**
 * Constructs a workspace-aware path
 * @param {string} path - The original path
 * @param {string} workspaceId - The current workspace ID
 * @returns {string} The workspace-prefixed path or original path
 */
export const getWorkspacePath = (path, workspaceId) => {

  // Return original path if no workspace ID
  if (!workspaceId) return path;
  
  // Return as-is if path already includes workspace ID
  if (path.includes(`/${workspaceId}`)) return path;
  
  // Don't prefix auth routes
  const authRoutes = ['/login', '/signup', '/forgot-password', '/reset-password', '/verify-email'];
  if (authRoutes.some(route => path.startsWith(route))) return path;
  
  // Don't prefix external URLs
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  
  // Handle query strings and hash fragments
  const [pathname, ...rest] = path.split(/[?#]/);
  const suffix = rest.length > 0 ? (path.includes('?') ? '?' : '#') + rest.join('#') : '';
  
  // Prepend workspace ID to pathname
  return `/${workspaceId}${pathname}${suffix}`;
};

/**
 * Extracts workspace ID from a path
 * @param {string} path - The path to extract from
 * @returns {string|null} The workspace ID or null
 */
export const extractWorkspaceId = (path) => {
  const pathParts = path.split('/');
  const firstSegment = pathParts[1];
  
  // Check if first segment is a valid workspace ID (24-char hex string)
  if (firstSegment && firstSegment.match(/^[a-fA-F0-9]{24}$/)) {
    return firstSegment;
  }
  
  return null;
};

/**
 * Removes workspace ID from a path
 * @param {string} path - The path to clean
 * @returns {string} The path without workspace ID
 */
export const removeWorkspaceFromPath = (path) => {
  const workspaceId = extractWorkspaceId(path);
  if (!workspaceId) return path;
  
  // Remove the workspace ID segment
  return path.replace(`/${workspaceId}`, '');
};